import type { TrackingLink, ThemeClasses } from "../types";
import {
  getStatusColor,
  getStatusText,
  getAccessTypeColor,
  getAccessTypeIcon,
  getAccessTypeText,
  trackingStatusKey,
} from "../utils/helpers";
import { useRealTimeCountdown } from "../hooks/useRealTimeCountdown";
import { DENSE_ROW, ICON_BTN, ICON_BTN_DANGER, STATUS_CHIP } from "../../../../shared";

interface TrackingLinkListRowProps {
  trackingLink: TrackingLink;
  themeClasses: ThemeClasses;
  onEdit: (trackingLink: TrackingLink) => void;
  onDelete: (trackingLink: TrackingLink) => void;
  onCopy: (trackingLink: TrackingLink) => void;
  onActivate: (trackingLink: TrackingLink) => void;
}

export function TrackingLinkListRow({
  trackingLink,
  themeClasses,
  onEdit,
  onDelete,
  onCopy,
  onActivate,
}: TrackingLinkListRowProps) {
  const statusKey = trackingStatusKey(trackingLink.status);
  const remainingTime = useRealTimeCountdown(trackingLink.expirationDate, statusKey);
  const isPending = statusKey === "pending";
  const isExpired = statusKey === "expired";
  const isInactive =
    statusKey === "inactive" ||
    trackingLink.activationType === "inactive";
  const hasNoUnits = !trackingLink.units || trackingLink.units.length === 0;

  const neutralBtn = `${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`;
  // Activar/Configurar son la única acción posible en ese estado: conservan su
  // texto, pero como botón de contorno de 32 px en vez de un bloque sólido.
  const textBtn = `h-8 px-2.5 border rounded-md text-xs font-medium transition-colors flex items-center gap-1`;

  const renderActionButtons = () => {
    if (isInactive) {
      return (
        <button
          onClick={() => onActivate(trackingLink)}
          className={`${textBtn} border-blue-500/40 text-blue-500 hover:bg-blue-500/10`}
        >
          <i className="ri-refresh-line text-sm"></i>
          <span>Activar</span>
        </button>
      );
    }

    if (isPending || hasNoUnits) {
      return (
        <button
          onClick={() => onEdit(trackingLink)}
          className={`${textBtn} border-primary-500/50 text-primary-500 hover:bg-primary-500/10`}
        >
          <i className="ri-settings-3-line text-sm"></i>
          <span>Configurar</span>
        </button>
      );
    }

    return (
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => onEdit(trackingLink)}
          className={neutralBtn}
          aria-label="Editar"
          title="Editar"
        >
          <i className="ri-pencil-line"></i>
        </button>
        <button
          onClick={() => onDelete(trackingLink)}
          className={ICON_BTN_DANGER}
          aria-label="Desactivar"
          title="Desactivar"
        >
          <i className="ri-close-circle-line"></i>
        </button>
        <button
          onClick={() => onCopy(trackingLink)}
          className={neutralBtn}
          aria-label="Copiar enlace"
          title="Copiar enlace"
        >
          <i className="ri-file-copy-line"></i>
        </button>
      </div>
    );
  };

  return (
    <div
      className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}
    >
      {/* Left Section: Icon + Name + Badges */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Icon */}
        <div className="flex-shrink-0 w-9 h-9 bg-primary-500/10 rounded-lg flex items-center justify-center">
          <i className="ri-link text-base text-primary-500"></i>
        </div>

        {/* Name and Badges */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`font-semibold text-sm ${themeClasses.text} truncate`}
            title={trackingLink.name}
          >
            {trackingLink.name}
          </span>

          {/* Status Badge */}
          <span
            className={`${STATUS_CHIP} ${getStatusColor(
              trackingLink.status,
            )}`}
          >
            {getStatusText(trackingLink.status)}
          </span>

          {/* Access Type Badge */}
          <span
            className={`${STATUS_CHIP} hidden sm:inline-flex gap-0.5 ${getAccessTypeColor(
              trackingLink.accessType,
            )}`}
          >
            <i
              className={`${getAccessTypeIcon(trackingLink.accessType)} text-xs`}
            ></i>
            <span>{getAccessTypeText(trackingLink.accessType)}</span>
          </span>
        </div>
      </div>

      {/* Center Section: Stats */}
      <div className="hidden md:flex items-center gap-4 shrink-0">
        {/* Expiration */}
        <div className="w-28 min-w-0">
          <div className={`text-[10px] ${themeClasses.textSecondary}`}>
            {isExpired ? "Expiró" : isPending ? "Vigencia" : "Tiempo restante"}
          </div>
          <div
            className={`text-xs font-mono font-medium ${
              remainingTime.isWarning
                ? "text-amber-500"
                : isExpired
                  ? "text-red-500"
                  : themeClasses.text
            }`}
          >
            {remainingTime.text}
          </div>
        </div>

        {/* Units */}
        <div className="w-20 min-w-0">
          <div className={`text-[10px] ${themeClasses.textSecondary}`}>
            Unidades
          </div>
          <div className={`text-xs font-medium ${themeClasses.text}`}>
            {trackingLink.unitsCount > 0
              ? `${trackingLink.unitsCount} vehículos`
              : "0"}
          </div>
        </div>

        {/* Access Count */}
        <div className="hidden lg:block w-16 min-w-0">
          <div className={`text-[10px] ${themeClasses.textSecondary}`}>
            Aperturas
          </div>
          <div className={`text-xs font-medium ${themeClasses.text}`}>
            {trackingLink.accessCount}
          </div>
        </div>
      </div>

      {/* Right Section: Action Buttons */}
      <div className="flex-shrink-0 flex justify-end">{renderActionButtons()}</div>
    </div>
  );
}
